import {
  tableErrors,
  type QuestionConfig,
  type AnswerValue,
  type SurveyQuestion,
  type TableCellValue,
  type TableColumn,
  type TableRow,
} from 'shared'

/** A copy, so editing a row in the form never reaches back into the question's config. */
function cloneRow(row: TableRow): TableRow {
  return { row_id: row.row_id, values: JSON.parse(JSON.stringify(row.values)) }
}

/**
 * The rows to show. A table nobody has touched shows the author's rows; once
 * there is an answer, the answer is the whole truth, including a row removed
 * from it (which the server would refuse for a fixed row, so it cannot happen).
 */
export function initialRows(question: SurveyQuestion, value: AnswerValue): TableRow[] {
  if (Array.isArray(value)) return value as TableRow[]
  return (question.config?.seedRows ?? []).map(cloneRow)
}

/**
 * Fills in the tables that have no stored answer with the author's rows, so the
 * form validates and sends what the person is looking at and not an empty list.
 */
export function withSeedRows(
  questions: SurveyQuestion[],
  answers: Record<string, AnswerValue>,
): Record<string, AnswerValue> {
  const filled = { ...answers }
  for (const question of questions) {
    if (question.type !== 'table') continue
    const stored = filled[question.id]
    if (stored === undefined || stored === null) {
      const seeds = initialRows(question, null)
      if (seeds.length > 0) filled[question.id] = seeds
    }
  }
  return filled
}

/** A short id that no existing row has. The prefix tells an author's row from a respondent's. */
export function newRowId(existing: TableRow[], prefix = 'N'): string {
  const taken = new Set(existing.map((row) => row.row_id))
  for (let attempt = 0; attempt < 50; attempt++) {
    const suffix =
      prefix === 'N'
        ? Math.random().toString(36).slice(2, 8)
        : String(existing.length + 1 + attempt).padStart(2, '0')
    const id = `${prefix}-${suffix}`
    if (!taken.has(id)) return id
  }
  return `${prefix}-${crypto.randomUUID().slice(0, 8)}`
}

export function blankRow(existing: TableRow[], prefix = 'N'): TableRow {
  return { row_id: newRowId(existing, prefix), values: {} }
}

export function isBlankCell(value: TableCellValue | undefined): boolean {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

export type RowState = 'ok' | 'part' | 'empty'

/**
 * Where a row stands: complete when the server would accept it, untouched when
 * the person has written nothing of their own, and in between otherwise.
 */
export function rowState(question: SurveyQuestion, row: TableRow): RowState {
  const columns = question.config?.columns ?? []
  const isSeed = (question.config?.seedRows ?? []).some((seed) => seed.row_id === row.row_id)
  const editable = columns.filter((column) => !(isSeed && column.lockedOnSeed))
  const written = editable.some((column) => !isBlankCell(row.values[column.key]))

  if (!written) return 'empty'

  const problems = tableErrors(question, [row], 'complete').filter(
    (error) => error.cell.row_id === row.row_id,
  )
  return problems.length === 0 ? 'ok' : 'part'
}

/** A key for a new column, from what the author called it, unique among the others. */
export function slugKey(label: string, taken: string[]): string {
  const base =
    label
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 24) || 'colonna'
  const start = /^[a-z]/.test(base) ? base : `c_${base}`
  if (!taken.includes(start)) return start
  for (let n = 2; n < 100; n++) {
    const candidate = `${start}_${n}`
    if (!taken.includes(candidate)) return candidate
  }
  return `${start}_${Date.now().toString(36)}`.slice(0, 30)
}

/** The label a stored cell shows, for a column with named options. */
export function cellText(column: TableColumn, value: TableCellValue | undefined): string {
  if (isBlankCell(value)) return ''
  const label = (entry: string) =>
    column.options?.find((option) => option.value === entry)?.label ?? entry
  if (Array.isArray(value)) return value.map(label).join(', ')
  if (typeof value === 'string') return column.type === 'short_text' ? value : label(value)
  return String(value)
}

/** What a new table starts with: one column, so the builder never shows an impossible table. */
export function defaultTableConfig(firstColumnLabel: string): QuestionConfig {
  return { columns: [{ key: slugKey(firstColumnLabel, []), label: firstColumnLabel, type: 'short_text' }] }
}

/** The row's values without one column: what a removed column leaves behind in the rows. */
export function withoutColumn(row: TableRow, key: string): TableRow {
  const values = { ...row.values }
  delete values[key]
  return { ...row, values }
}
